/** The device viewport the renderer draws into.
 *
 * An iframe with `sandbox="allow-same-origin"`, not a shadow root. The origin is
 * preserved, so `font-src 'self'` matches and the self-hosted Roboto actually loads —
 * with a bare `sandbox=""` the frame gets an opaque origin and the font silently falls
 * back to a system face, which is fatal for a tool whose whole job is fidelity. An
 * iframe also gives a real viewport and keeps operator-authored HTML out of the
 * console's own document.
 *
 * The preview tree is a pure function of (payload, device, renderer), so it gets its
 * OWN React root inside the frame rather than a portal: no cross-document event
 * bubbling to reason about, and nothing is shared that would need to be.
 *
 * Drawn at 1dp = 1 CSS px so every number transcribed from the Android layouts stays
 * literal, then scaled to fit the pane.
 *
 * A crash inside the frame's own root never reaches the page's error boundaries on its
 * own (a separate root): `FrameCatch` catches it there and DeviceFrame rethrows it in the
 * page's tree, where the preview's boundary (`PreviewErrorBoundary`) says so with Retry
 * (C27).
 */
import { Box } from '@mui/material';
import { Component, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';

import type { DeviceProfile } from './types';

/** A minimal reset. Deliberately not a full normalize: the item CSS states everything.
 *
 *  `height: 100%` on html AND body is load-bearing, not tidiness. The screens are phone
 *  layouts: each root is `height: 100%` and the tour screen's children — map, toolbar,
 *  bottom sheet — are ALL absolutely positioned. A percentage height resolves against an
 *  auto-height parent as auto, so without this the tour root computed to 0px and
 *  `overflow: hidden` clipped the entire screen away. Measured in Chrome: 0px without,
 *  892px with. The itinerary screen survived only by accident — its scroller is in flow,
 *  so the root inherited a content height — and scrolled the whole frame body rather than
 *  its own list. jsdom does no layout, so nothing in the test suite can catch this. */
const RESET = `
*, *::before, *::after { box-sizing: border-box; }
html, body { margin: 0; padding: 0; height: 100%; }
body {
  font-family: var(--and-font-stack, Roboto, system-ui, sans-serif);
  -webkit-font-smoothing: antialiased;
  background: #ffffff;
  color: #000000;
  overflow-x: hidden;
}
img { display: block; max-width: 100%; }
a { color: inherit; }
[data-preview-root] { height: 100%; }
`;

/** Unmount a root whose container may already be gone.
 *
 *  Both call sites unmount off-cycle, by which time the container can have been detached
 *  — the document was swapped, or the host tore the iframe down first. React then throws
 *  NotFoundError removing nodes from a body that is no longer their parent. There is
 *  nothing to clean up in that case, and letting it escape a `setTimeout` makes it an
 *  unhandled rejection that masks real errors in a test run. */
function discard(root: Root) {
  try {
    root.unmount();
  } catch {
    /* container already detached — nothing left to unmount */
  }
}

/** The frame root's boundary: a renderer that throws (a screen, a mapper) renders nothing in
 *  the frame and hands the error to the page's tree (`onCrash`), whose boundary says so. */
class FrameCatch extends Component<{ onCrash: (error: Error) => void; children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: Error): void {
    this.props.onCrash(error);
  }

  override render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}

/** The scale that fits the phone into the width it has — and, when given, the height
 *  (the tour dock: the followed day must be visible without scrolling the dock). Never
 *  above 1:1. A height too small to be useful (under `MIN_FIT_HEIGHT_PX`) is ignored. */
export const MIN_FIT_HEIGHT_PX = 360;
export function fitScale(availableWidth: number, maxHeightPx: number | undefined, device: Pick<DeviceProfile, 'widthDp' | 'heightDp'>): number {
  const byWidth = availableWidth > 0 ? availableWidth / device.widthDp : 1;
  const byHeight = maxHeightPx && maxHeightPx >= MIN_FIT_HEIGHT_PX ? maxHeightPx / device.heightDp : Infinity;
  return Math.min(1, byWidth, byHeight);
}

export interface DeviceFrameProps {
  device: DeviceProfile;
  /** Token stylesheets to inject (CSS text, not URLs). */
  styles: string[];
  /** Scale to fit the available width, rather than drawing 1:1. */
  fit?: boolean;
  /** Fit the height too: the most the phone may take (px). See `fitScale`. */
  maxHeightPx?: number;
  /** A layer drawn in the PARENT document, behind the frame, inside the same scaled and
   *  clipped box — so the device transform, rounded corners and overflow clip apply to
   *  it for free. Used for the tour map, which cannot run inside the sandboxed frame.
   *  Supplying one makes the frame document transparent so the underlay shows through;
   *  each screen then paints its own ground. */
  underlay?: ReactNode;
  children: ReactNode;
}

export function DeviceFrame({ device, styles, fit = true, maxHeightPx, underlay, children }: DeviceFrameProps) {
  // The styles depend on WHETHER there is an underlay, not on the element: the Shell
  // passes a new one every render, which used to strip and re-insert every <style> in
  // the frame (the Roboto faces included) on each render.
  const hasUnderlay = Boolean(underlay);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const rootRef = useRef<Root | null>(null);
  const docRef = useRef<Document | null>(null);
  // The teardown's deferred unmount, until it runs: StrictMode's simulated unmount and remount
  // (dev) cancels it and keeps the root, rather than building a second root on the same body
  // — two roots on one container is React's createRoot warning and an uncaught `removeChild`
  // NotFoundError when the old one unmounts (C27).
  const pendingDiscard = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A crash inside the frame's root, rethrown in this tree (below).
  const [crash, setCrash] = useState<Error | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [attached, setAttached] = useState(0);
  const [scale, setScale] = useState(1);
  const [availableWidth, setAvailableWidth] = useState(0);

  // Inject styles into the frame document and own a React root on its body.
  //
  // The subtlety that cost us a blank preview: `srcDoc` does NOT populate the document
  // that exists when this effect first runs. The browser creates a transient about:blank
  // document synchronously — it has a body and `readyState === 'complete'`, so any
  // "is it ready?" check passes — and then REPLACES it once the srcdoc parses. Anything
  // written into the first document is silently discarded with it, and a React root left
  // pointing at that detached body renders to nothing, with no error anywhere. Verified in
  // Chrome: after the swap, `contentDocument`, `body`, injected styles and appended nodes
  // are all gone. jsdom does not model the swap at all, so no test here can catch it.
  //
  // So key on DOCUMENT IDENTITY, not on readiness: attach now, attach again on load, and
  // whenever the document underneath us has changed, abandon the old root and build a new
  // one on the live body.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    const attach = () => {
      const doc = iframe.contentDocument;
      if (!doc?.body) return;

      if (docRef.current !== doc) {
        const stale = rootRef.current;
        rootRef.current = null;
        docRef.current = doc;
        // Its container is gone; unmount off-cycle for the same reason as teardown below.
        if (stale) setTimeout(() => discard(stale), 0);
      }

      doc.head.querySelectorAll('style[data-preview]').forEach((n) => n.remove());
      for (const css of [RESET, ...styles]) {
        const el = doc.createElement('style');
        el.setAttribute('data-preview', '');
        el.textContent = css;
        doc.head.appendChild(el);
      }

      // With an underlay the frame must not paint its own ground, or it hides the map.
      if (hasUnderlay) {
        const el = doc.createElement('style');
        el.setAttribute('data-preview', '');
        el.textContent = 'html, body { background: transparent !important; }';
        doc.head.appendChild(el);
      }

      // The root's container is a div of ours, not the body: React warns against a body
      // root, and a frame document torn down with the page can empty its body before the
      // deferred unmount runs — whose removeChild then throws NotFoundError. Our div keeps
      // its children whatever happens to the body. `height: 100%` (RESET) carries the
      // body's height down to the screens.
      if (!rootRef.current) {
        const host = doc.createElement('div');
        host.setAttribute('data-preview-root', '');
        doc.body.appendChild(host);
        rootRef.current = createRoot(host);
      }
      setAttached((n) => n + 1);
    };

    attach();
    // NOT `once`: the first call lands on the throwaway document, the load lands on the
    // real one — and a styles change can re-run this effect after both.
    iframe.addEventListener('load', attach);
    return () => iframe.removeEventListener('load', attach);
    // styles is a stable array from the renderer module
  }, [styles, hasUnderlay]);

  // Render (and re-render) the tree inside the frame. `attached` counts re-attachments,
  // so a new root after a document swap gets the tree even though `children` is unchanged.
  useEffect(() => {
    if (attached === 0 || !rootRef.current) return;
    rootRef.current.render(<FrameCatch onCrash={setCrash}>{children}</FrameCatch>);
  }, [attached, children]);

  // Unmount the inner root on teardown, asynchronously: React forbids unmounting a root
  // synchronously from inside a lifecycle of the tree that owns it. A remount before it runs
  // (StrictMode, dev) cancels it: the root, its document and its tree stay.
  useEffect(() => {
    if (pendingDiscard.current !== null) {
      clearTimeout(pendingDiscard.current);
      pendingDiscard.current = null;
    }
    return () => {
      const root = rootRef.current;
      if (!root) return;
      pendingDiscard.current = setTimeout(() => {
        pendingDiscard.current = null;
        if (rootRef.current === root) {
          rootRef.current = null;
          docRef.current = null;
        }
        discard(root);
      }, 0);
    };
  }, []);

  // Scale to fit the pane. Measured, because the pane is user-resizable.
  useLayoutEffect(() => {
    if (!fit) {
      setScale(1);
      return undefined;
    }
    const el = wrapperRef.current;
    if (!el) return undefined;
    const measure = () => {
      const available = el.clientWidth;
      setAvailableWidth(available);
      setScale(fitScale(available, maxHeightPx, device));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fit, device, maxHeightPx]);
  // The frame's renderer crashed: the page's boundary takes it from here.
  if (crash) throw crash;

  // Fitted by height, the phone is narrower than its column: centre it.
  const offsetX = availableWidth > 0 ? Math.max(0, (availableWidth - device.widthDp * scale) / 2) : 0;

  return (
    <Box ref={wrapperRef} sx={{ width: '100%', overflow: 'hidden' }}>
      <Box
        sx={{
          width: device.widthDp,
          height: device.heightDp,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          marginLeft: `${offsetX}px`,
          // keep the flow box the size the scaled frame actually occupies
          marginBottom: `${device.heightDp * (scale - 1)}px`,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: underlay ? 'transparent' : '#fff',
          position: 'relative',
        }}
      >
        {underlay && (
          <Box sx={{ position: 'absolute', inset: 0 }} data-testid="device-frame-underlay">
            {underlay}
          </Box>
        )}
        <Box
          component="iframe"
          ref={iframeRef}
          title="Mobile preview"
          sandbox="allow-same-origin"
          srcDoc="<!doctype html><html><head></head><body></body></html>"
          sx={{
            width: '100%',
            height: '100%',
            border: 0,
            display: 'block',
            position: 'relative',
            background: 'transparent',
          }}
        />
      </Box>
    </Box>
  );
}
